import { Vector2 } from './ImageAnnotations.classes';

const DOT_RADII = {
  unSelected: 6,
  selected: 10,
};

/**
 * Grabs the computed dimensions of an HTML element
 *
 * @param canvas
 * @return Vector2
 */
export const getComputedDimensions = (canvas) => {
  const wrapperStyles = getComputedStyle(canvas);

  return new Vector2(
    Number(wrapperStyles.width.split('px')[0]),
    Number(wrapperStyles.height.split('px')[0]),
  );
};

/**
 * Loads an image, then draws the content onto the given context.
 * Also creates and sets an "image context", this allows us to re-draw
 * the image content later for repaints when needed as drawing anything
 * on top of the image is a destructive process.
 *
 * @param imgSrc string
 * @param context CanvasRenderingContext2D
 * @param setImageCtx CanvasRenderingContext2D
 */
export const loadImage = (
  imgSrc,
  context,
  setImageCtx,
) => {
  const img = new Image();

  img.onload = () => {
    /**
     * Get dimensions based on computed styles of canvas element. This will return a Vector2 with
     * the computes size of the canvas so we know our width constraints.
     */
    const dimensions = getComputedDimensions(context.canvas);

    // Set canvas size to match our newly computed dimensions.
    const resizedWidth = dimensions.x * window.devicePixelRatio;
    const resizedHeight = (img.naturalHeight / img.naturalWidth) * resizedWidth;
    context.canvas.width = resizedWidth;
    context.canvas.height = resizedHeight;

    // Clear the canvas content starting at 0,0 and ending at the new height and width
    context.clearRect(0, 0, resizedWidth, resizedHeight);

    // Draw the image content onto the canvas
    context.drawImage(img, 0, 0, resizedWidth, resizedHeight);

    // Create a new canvas element but dont add it to the DOM
    const imageCanvas = document.createElement('canvas');
    imageCanvas.width = resizedWidth;
    imageCanvas.height = resizedHeight;

    /**
     * Get the context for the new canvas, draw the image content onto this "temporary" canvas
     * for safekeeping.
     */
    const imgCtx = imageCanvas.getContext('2d');
    if (imgCtx) {
      imgCtx.drawImage(img, 0, 0, resizedWidth, resizedHeight);
      setImageCtx(imgCtx);
    }
  };

  img.src = imgSrc;
};

export const getDistance = (
  v1,
  v2,
) => {
  if (!v1 || !v2) {
    return Number.MAX_SAFE_INTEGER;
  }

  return Math.sqrt(
    ((v1.x - v2.x) ** 2) + ((v1.y - v2.y) ** 2),
  );
};

/**
 * Return the x and y position of a click or hover event
 * @param e
 */
export const getRelativePosition = (e) => {
  const rect = e.target.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  return new Vector2(x, y);
};

export const getPositionFromPercentage = (
  ctx,
  percentagePosition,
) => {
  if (!ctx || !percentagePosition) {
    return new Vector2(0, 0);
  }

  return new Vector2(
    (percentagePosition.x * ctx.canvas.width) / window.devicePixelRatio,
    (percentagePosition.y * ctx.canvas.height) / window.devicePixelRatio,
  );
};

export const getPercentageFromPosition = (
  position,
  ctx,
) => {
  if (!ctx || !position) {
    return new Vector2(0, 0);
  }

  return new Vector2(
    (position.x / ctx.canvas.width) * window.devicePixelRatio,
    (position.y / ctx.canvas.height) * window.devicePixelRatio,
  );
};

/**
 * Returns the annotation at a given position on the canvas
 * @param annotations Annotation[]
 * @param position Vector2
 */
export const getAnnotationAtPosition = (
  annotations,
  position,
  ctx,
) => {
  if (!annotations || !ctx || !position) {
    return undefined;
  }

  return annotations.find((annotation) => {
    const distance = getDistance(
      getPositionFromPercentage(
        ctx,
        new Vector2(
          annotation.xCoordinate,
          annotation.yCoordinate,
        ),
      ),
      position,
    );

    return distance <= DOT_RADII.selected * 1.05;
  });
};

/**
 * Draws the image onto canvas, then draws all of the annotations on top of that.
 * This function is likely to be run very rapidly, so avoid expensive processing
 * in here to ensure smoothness of movement.
 *
 * @param ctx CanvasRenderingContext2D
 * @param imageCtx CanvasRenderingContext2D
 * @param annotations Annotation[]
 */
export const draw = (
  ctx,
  imageCtx,
  annotations,
  hoveredAnnotation,
) => {
  if (ctx && imageCtx !== null) {
    // Clear all the content from the canvas
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Redraw the image from the temporary image canvas
    ctx.drawImage(imageCtx.canvas, 0, 0);

    if (!annotations) {
      return;
    }

    annotations.forEach((annotation) => {
      // Configure drop shadow settings
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowOffsetY = 5;
      ctx.shadowBlur = 5;
      ctx.fillStyle = 'white';
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 0;
      let radius = DOT_RADII.unSelected;

      if (hoveredAnnotation && annotation.id === hoveredAnnotation.id) {
        radius = DOT_RADII.selected;
      }

      // beginPath starts a new path and ends the old one
      ctx.beginPath();

      // Draw an arc (a full arc is a circle) at the annotation position
      ctx.arc(
        annotation.xCoordinate * ctx.canvas.width,
        annotation.yCoordinate * ctx.canvas.height,
        radius * window.devicePixelRatio,
        0,
        2 * Math.PI,
      );

      // Add fill to the arc
      ctx.fill();

      if (hoveredAnnotation && annotation.id === hoveredAnnotation.id) {
        // Add stroke to the arc
        ctx.stroke();
      }
    });
  }
};

export const handleMouseMove = (
  ctx,
  setMousePosition
) => (
  e,
) => {
  const mousePosition = getRelativePosition(e);

  setMousePosition(mousePosition);
};

export const handleClick = (
  ctx,
  annotations,
  hoveredAnnotation,
  setHoveredAnnotation
) => (
  e,
) => {
  const mousePosition = getRelativePosition(e);
  const annotation = getAnnotationAtPosition(annotations, mousePosition, ctx);

  if (hoveredAnnotation && annotation.id === hoveredAnnotation.id) {
    return setHoveredAnnotation(null);
  }

  return setHoveredAnnotation(annotation);
};
